// the privacy policy page - basically just a big wall of static legal text
import React from 'react';
// Link is react-router's way of navigating without a full page reload
import { Link } from 'react-router-dom';
// shared styles for both legal pages (privacy + terms)
import './LegalPage.css';
import { usePageMeta } from '../hooks/usePageMeta';
// bump this date whenever the policy actually changes; it's shown near the title
const LAST_UPDATED = 'August 4, 2026';

export default function PrivacyPage() {
  usePageMeta({
    title: 'Privacy Policy - GPTDisguise',
    description: 'How GPTDisguise handles your data. The extension runs entirely in your browser: no server, no tracking, no collection of your conversations.',
    path: '/privacy'
  });

  // there's no logic here, we just render the same static content every time
  return (
    <div className="legal-page">
      {/* NAV - simple top bar with the logo and a back-to-home link */}
      <nav className="legal-nav">
        <a href="/" className="legal-nav-logo">
          <img src="/icon.png" alt="GPTDisguise icon" className="legal-nav-icon" />
          GPT<span>Disguise</span>
        </a>
        <Link to="/" className="legal-nav-back">← Back to home</Link>
      </nav>

      {/* the actual policy - title, the last-updated date, then a bunch of sections */}
      <main className="legal-content">
        <p className="legal-eyebrow">Legal</p>
        <h1>Privacy Policy</h1>
        {/* pulls in the date constant from up top so we only edit it in one place */}
        <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

        <p className="legal-lead">
          GPTDisguise is built privacy-first. The browser extension renders its
          disguise entirely on your device: your conversations, prompts, and the
          content of any AI chat never pass through our servers. This policy
          explains the limited data we do handle to run accounts, billing, and the
          website.
        </p>

        <section>
          <h2>1. The extension collects nothing</h2>
          <p>
            The GPTDisguise browser extension works with pure CSS and JavaScript
            running locally in your browser. It does not transmit, log, sell, or
            store the content you type into ChatGPT, Claude, Gemini, or any other
            site. There is no analytics, no tracking pixel, and no telemetry inside
            the extension.
          </p>
        </section>

        <section>
          <h2>2. Information we collect</h2>
          <p>We only collect what we need to operate accounts and Premium billing:</p>
          <ul>
            <li>
              <strong>Account information.</strong> When you sign up, our
              authentication provider (Supabase) stores your email address and, if
              you sign in with Google, your name and profile basics returned by
              Google.
            </li>
            <li>
              <strong>Subscription status.</strong> Whether your plan is Free or
              Premium, so the extension can unlock the right features.
            </li>
            <li>
              <strong>Payment information.</strong> Payments are processed by
              Stripe. We never see or store your full card number - Stripe handles
              that and shares only a customer/subscription identifier and status
              with us.
            </li>
          </ul>
        </section>

        <section>
          <h2>3. How we use information</h2>
          <ul>
            <li>To create and secure your account and let you sign in.</li>
            <li>To determine which features (Free or Premium) you can access.</li>
            <li>To process subscription payments and prevent fraud, via Stripe.</li>
            <li>To send essential account and billing emails. We do not send marketing email without your consent.</li>
          </ul>
        </section>

        <section>
          <h2>4. Service providers</h2>
          <p>We rely on a small number of trusted processors:</p>
          <ul>
            <li><strong>Supabase</strong>: authentication and account storage.</li>
            <li><strong>Stripe</strong>: subscription billing and payment processing.</li>
            <li><strong>Vercel</strong>: website and API hosting.</li>
          </ul>
          <p>
            Each processes data only to provide their service to us and under their
            own privacy and security commitments.
          </p>
        </section>

        <section>
          <h2>5. What we never do</h2>
          <ul>
            <li>We do not sell or rent your personal information.</li>
            <li>We do not read, store, or analyze your AI conversations.</li>
            <li>We do not use third-party advertising trackers.</li>
          </ul>
        </section>

        <section>
          <h2>6. Data retention</h2>
          <p>
            We keep account and billing information for as long as your account is
            active. If you delete your account, we remove your account record;
            limited billing records may be retained where required by law or by
            Stripe for tax and accounting purposes.
          </p>
        </section>

        <section>
          <h2>7. Your rights</h2>
          <p>
            Depending on where you live, you may have the right to access, correct,
            export, or delete your personal data. To exercise any of these rights,
            contact us and we will respond within a reasonable timeframe. 
          </p>
        </section>

        <section>
          <h2>8. Children</h2>
          <p>
            GPTDisguise is not directed to children under 13, and we do not
            knowingly collect personal information from them.
          </p>
        </section>

        <section>
          <h2>9. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. When we do, we will revise
            the “Last updated” date above. Material changes will be communicated
            through the website or by email.
          </p>
        </section>

        <section>
          <h2>10. Contact</h2>
          <p>
            Questions about this policy or your data? Email{' '}
            <a href="mailto:cloakstudios@outlook.com">cloakstudios@outlook.com</a>.
          </p>
        </section>

        <p className="legal-footnote">
          GPTDisguise · Cloak Studio · Not affiliated with OpenAI, Anthropic,
          Google, or Microsoft.
        </p>
      </main>
    </div>
  );
}
